// Operations Command Center - Hospice CareLens
// Real-time autonomous operations dashboard with WebSocket updates
// Phase 3: Historical Trends & Analytics

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Brain,
  Users,
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Clock,
  Target,
  Activity,
  Zap,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  Loader2,
  UserPlus,
  Calendar,
  BarChart3,
  PieChart,
  Heart,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Building,
  Briefcase,
  Shield,
  Flame,
  LineChart,
  Settings,
  Bell,
  CheckCircle2,
  XCircle,
  Info,
  Plus,
  Edit3,
  Save,
  FlaskConical,
  Sparkles,
  Calculator,
  Play,
  RotateCcw,
  FileInput,
  ClipboardList,
  History,
  Download,
  Flag,
  Milestone,
  Bot,
  Timer
} from 'lucide-react';
import { LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, Legend } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Severity/Status colors
const STATUS_COLORS = {
  critical: 'bg-red-500/20 text-red-400 border-red-500/30',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  medium: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  low: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  urgent: 'bg-red-500/20 text-red-400 border-red-500/30',
  planned: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  none: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  on_track: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  behind: 'bg-red-500/20 text-red-400 border-red-500/30',
  met: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  exceeded: 'bg-red-500/20 text-red-400 border-red-500/30',
  below: 'bg-amber-500/20 text-amber-400 border-amber-500/30'
};

const BURNOUT_COLORS = {
  low: { bg: 'bg-emerald-500', text: 'text-emerald-400', label: 'Low Risk' },
  moderate: { bg: 'bg-amber-500', text: 'text-amber-400', label: 'Moderate Risk' },
  high: { bg: 'bg-orange-500', text: 'text-orange-400', label: 'High Risk' },
  critical: { bg: 'bg-red-500', text: 'text-red-400', label: 'Critical Risk' }
};

export default function OperationsCommandCenter() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [wsConnected, setWsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const wsRef = useRef(null);

  // Data Entry State
  const [showDataEntry, setShowDataEntry] = useState(false);
  const [dataEntryType, setDataEntryType] = useState('census');
  const [dataEntryLoading, setDataEntryLoading] = useState(false);
  const [censusForm, setCensusForm] = useState({
    date: new Date().toISOString().split('T')[0],
    adc: '',
    admissions: '',
    discharges: '',
    deaths: ''
  });
  const [staffForm, setStaffForm] = useState({
    name: '',
    role: 'rn',
    fte: '1.0',
    team: ''
  });
  const [productivityForm, setProductivityForm] = useState({
    clinician_name: '',
    team: '',
    discipline: 'RN',
    total_visits: '',
    overtime_hours: ''
  });

  // What-If Simulation State
  const [showSimulation, setShowSimulation] = useState(false);
  const [simulationLoading, setSimulationLoading] = useState(false);
  const [simulationParams, setSimulationParams] = useState({
    adc: 95,
    rn_fte: 5,
    lpn_fte: 5,
    aide_fte: 5,
    daily_rate: 185
  });
  const [simulationResults, setSimulationResults] = useState(null);

  // Analytics State (Phase 3)
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsTimeRange, setAnalyticsTimeRange] = useState('30');
  const [analyticsViewMode, setAnalyticsViewMode] = useState('weekly'); // 'weekly' or 'daily'
  const [weeklyData, setWeeklyData] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  // Staff Analytics State
  const [staffBurnoutData, setStaffBurnoutData] = useState(null);
  const [staffPrnData, setStaffPrnData] = useState(null);
  const [staffDataLoading, setStaffDataLoading] = useState(false);

  // Data Quality State
  const [dataQualityReport, setDataQualityReport] = useState(null);
  const [dataQualityLoading, setDataQualityLoading] = useState(false);
  const [fixLoading, setFixLoading] = useState({});
  const [integrityCheck, setIntegrityCheck] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);

  // Automation & Trust State
  const [trustScorecard, setTrustScorecard] = useState(null);
  const [trustLoading, setTrustLoading] = useState(false);

  const token = localStorage.getItem('qapi-token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  // Fetch operations summary
  const fetchSummary = useCallback(async (showToast = false) => {
    try {
      const res = await fetch(`${API_URL}/api/operations/summary`, { headers });
      const data = await res.json();
      
      if (data.success) {
        setSummary(data.summary);
        setLastUpdate(new Date());
        if (showToast) {
          toast.success('Data refreshed');
        }
      }
    } catch (error) {
      console.error('Failed to fetch summary:', error);
      toast.error('Failed to load operations data');
    }
  }, []);

  // Fetch alerts
  const fetchAlerts = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/operations/alerts?limit=10`, { headers });
      const data = await res.json();
      
      if (data.success) {
        setAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  }, []);

  // Fetch analytics data (Phase 3)
  const fetchAnalytics = useCallback(async (days = 30) => {
    setAnalyticsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/operations/history/summary?days=${days}`, { headers });
      const data = await res.json();
      
      if (data.success) {
        setAnalyticsData(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  // Fetch weekly summary data
  const fetchWeeklyData = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/operations/history/weekly`, { headers });
      const data = await res.json();
      
      if (data.success) {
        setWeeklyData(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch weekly data:', error);
    }
  }, []);

  // Export historical data
  const exportData = async (metricType, format = 'csv') => {
    setExportLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/operations/history/export/${metricType}?days=${analyticsTimeRange}&format=${format}`, { headers });
      const data = await res.json();
      
      if (data.success) {
        if (format === 'csv' && data.csv) {
          // Create and download CSV
          const blob = new Blob([data.csv], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = data.filename || `${metricType}_export.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          toast.success(`Exported ${data.record_count} records`);
        } else {
          // JSON download
          const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = data.filename || `${metricType}_export.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          toast.success(`Exported ${data.record_count} records`);
        }
      }
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  // Fetch staff burnout analysis
  const fetchStaffBurnout = useCallback(async () => {
    setStaffDataLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/operations/staff/burnout-analysis`, { headers });
      const data = await res.json();
      
      if (data.success) {
        setStaffBurnoutData(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch staff burnout:', error);
    } finally {
      setStaffDataLoading(false);
    }
  }, []);

  // Fetch staff PRN analysis
  const fetchStaffPrn = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/operations/staff/prn-analysis`, { headers });
      const data = await res.json();
      
      if (data.success) {
        setStaffPrnData(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch staff PRN:', error);
    }
  }, []);

  // Fetch data quality report
  const fetchDataQuality = useCallback(async () => {
    setDataQualityLoading(true);
    try {
      const [qualityRes, integrityRes] = await Promise.all([
        fetch(`${API_URL}/api/data-entry/quality-report`, { headers }),
        fetch(`${API_URL}/api/data-sync/integrity-check`, { headers })
      ]);
      
      const qualityData = await qualityRes.json();
      if (qualityData.success) {
        setDataQualityReport(qualityData);
      }
      
      if (integrityRes.ok) {
        const integrityData = await integrityRes.json();
        setIntegrityCheck(integrityData);
      }
    } catch (error) {
      console.error('Failed to fetch data quality:', error);
      toast.error('Failed to load data quality report');
    } finally {
      setDataQualityLoading(false);
    }
  }, []);

  // Sync all data for integrity
  const runIntegritySync = async () => {
    setSyncLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/data-sync/integrity-sync`, {
        method: 'POST',
        headers
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success(`Data integrity sync complete! Score: ${data.pre_sync_score}% → ${data.post_sync_score}%. ${data.fixes_applied} fixes applied, ${data.cascade_calculations?.length || 0} calculations re-triggered.`);
        // Refresh everything
        await fetchDataQuality();
        await fetchSummary();
      } else {
        toast.error('Sync failed');
      }
    } catch (error) {
      toast.error('Failed to run integrity sync');
    } finally {
      setSyncLoading(false);
    }
  };

  // Fetch Trust Scorecard
  const fetchTrustScorecard = useCallback(async () => {
    setTrustLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/operational-trust/scorecard`, { headers });
      if (res.ok) {
        setTrustScorecard(await res.json());
      }
    } catch (error) {
      console.error('Failed to fetch trust scorecard:', error);
    } finally {
      setTrustLoading(false);
    }
  }, []);

  // Fix data issue
  const fixDataIssue = async (fixAction) => {
    setFixLoading(prev => ({ ...prev, [fixAction]: true }));
    try {
      const res = await fetch(`${API_URL}/api/data-entry/fix/${fixAction}`, {
        method: 'POST',
        headers
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success(data.message);
        // Refresh data quality report
        await fetchDataQuality();
        // Also refresh operations data
        await fetchSummary();
      } else {
        toast.error(data.error || 'Fix failed');
      }
    } catch (error) {
      toast.error('Failed to apply fix');
    } finally {
      setFixLoading(prev => ({ ...prev, [fixAction]: false }));
    }
  };

  // Merge staff
  const mergeStaff = async (keepName, removeName) => {
    setFixLoading(prev => ({ ...prev, 'merge': true }));
    try {
      const res = await fetch(`${API_URL}/api/data-entry/fix/merge-staff`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ keep_name: keepName, remove_name: removeName })
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success(data.message);
        await fetchDataQuality();
        await fetchStaffBurnout();
      } else {
        toast.error(data.error || 'Merge failed');
      }
    } catch (error) {
      toast.error('Failed to merge staff');
    } finally {
      setFixLoading(prev => ({ ...prev, 'merge': false }));
    }
  };

  // Force recalculation
  const forceRecalculation = async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/api/operations/recalculate`, {
        method: 'POST',
        headers
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success(`Recalculated ${Object.keys(data.result?.calculations || {}).length} metrics`);
        await fetchSummary();
        await fetchAlerts();
      }
    } catch (error) {
      toast.error('Recalculation failed');
    } finally {
      setRefreshing(false);
    }
  };

  // ==================== DATA ENTRY FUNCTIONS ====================
  
  const submitCensusEntry = async () => {
    if (!censusForm.adc) {
      toast.error('Please enter ADC value');
      return;
    }
    
    setDataEntryLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/data-entry/census`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          date: censusForm.date,
          adc: parseFloat(censusForm.adc),
          admissions: parseInt(censusForm.admissions) || 0,
          discharges: parseInt(censusForm.discharges) || 0,
          deaths: parseInt(censusForm.deaths) || 0
        })
      });
      const data = await res.json();
      
      if (data.success) {
        const mergeMsg = data.merged ? ' (merged with existing record)' : '';
        toast.success(`Census ${data.action}${mergeMsg}! ${data.cascade?.calculations_run?.length || 0} calculations triggered.`);
        setCensusForm({ date: new Date().toISOString().split('T')[0], adc: '', admissions: '', discharges: '', deaths: '' });
        setShowDataEntry(false);
        await fetchSummary();
        await fetchAlerts();
      } else {
        toast.error(data.detail || 'Failed to save census');
      }
    } catch (error) {
      toast.error('Failed to save census data');
    } finally {
      setDataEntryLoading(false);
    }
  };

  const submitStaffEntry = async () => {
    if (!staffForm.name) {
      toast.error('Please enter staff name');
      return;
    }
    
    setDataEntryLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/data-entry/staff`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: staffForm.name,
          role: staffForm.role,
          fte: parseFloat(staffForm.fte),
          team: staffForm.team,
          status: 'active'
        })
      });
      const data = await res.json();
      
      if (data.success) {
        const mergeMsg = data.merged ? ' (updated existing)' : '';
        toast.success(`Staff ${data.action}${mergeMsg}! Capacity and hiring predictions updated.`);
        setStaffForm({ name: '', role: 'rn', fte: '1.0', team: '' });
        setShowDataEntry(false);
        await fetchSummary();
        await fetchAlerts();
      } else {
        toast.error(data.detail || 'Failed to add staff');
      }
    } catch (error) {
      toast.error('Failed to add staff');
    } finally {
      setDataEntryLoading(false);
    }
  };

  const submitProductivityEntry = async () => {
    if (!productivityForm.clinician_name || !productivityForm.total_visits) {
      toast.error('Please fill required fields');
      return;
    }
    
    setDataEntryLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/data-entry/productivity`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          clinician_name: productivityForm.clinician_name,
          team: productivityForm.team,
          discipline: productivityForm.discipline,
          period_start: new Date().toISOString().split('T')[0],
          total_visits: parseInt(productivityForm.total_visits),
          overtime_hours: parseFloat(productivityForm.overtime_hours) || 0
        })
      });
      const data = await res.json();
      
      if (data.success) {
        const mergeMsg = data.merged ? ' (merged with existing)' : '';
        const staffMsg = data.staff_action === 'created' ? ' Staff added to roster.' : '';
        toast.success(`Productivity ${data.action}${mergeMsg}.${staffMsg} Burnout risk: ${data.cascade?.burnout_risk || 'updated'}`);
        setProductivityForm({ clinician_name: '', team: '', discipline: 'RN', total_visits: '', overtime_hours: '' });
        setShowDataEntry(false);
        await fetchSummary();
        await fetchAlerts();
      } else {
        toast.error(data.detail || 'Failed to save productivity');
      }
    } catch (error) {
      toast.error('Failed to save productivity data');
    } finally {
      setDataEntryLoading(false);
    }
  };

  // ==================== WHAT-IF SIMULATION FUNCTIONS ====================
  
  const runSimulation = async () => {
    setSimulationLoading(true);
    
    // Calculate projections based on simulation parameters
    const { adc, rn_fte, lpn_fte, aide_fte, daily_rate } = simulationParams;
    
    // Staffing benchmarks
    const benchmarks = {
      rn: { patients_per_fte: 12 },
      lpn: { patients_per_fte: 15 },
      aide: { patients_per_fte: 8 }
    };
    
    // Calculate required FTEs
    const required_rn = adc / benchmarks.rn.patients_per_fte;
    const required_lpn = adc / benchmarks.lpn.patients_per_fte;
    const required_aide = adc / benchmarks.aide.patients_per_fte;
    
    // Calculate gaps
    const rn_gap = required_rn - rn_fte;
    const lpn_gap = required_lpn - lpn_fte;
    const aide_gap = required_aide - aide_fte;
    const total_gap = Math.max(0, rn_gap) + Math.max(0, lpn_gap) + Math.max(0, aide_gap);
    
    // Revenue projections
    const daily_revenue = adc * daily_rate;
    const monthly_revenue = daily_revenue * 30;
    const annual_revenue = daily_revenue * 365;
    
    // Hiring cost estimate
    const avg_salary = 65000;
    const hiring_cost = total_gap * avg_salary;
    
    // Caseload calculations
    const total_clinical_fte = rn_fte + lpn_fte;
    const avg_caseload = total_clinical_fte > 0 ? adc / total_clinical_fte : 0;
    
    // Burnout risk calculation
    const caseload_factor = Math.min((avg_caseload / 12) * 40, 40);
    const burnout_score = caseload_factor;
    const burnout_level = burnout_score >= 70 ? 'critical' : burnout_score >= 50 ? 'high' : burnout_score >= 30 ? 'moderate' : 'low';
    
    // Simulate delay for UX
    await new Promise(resolve => setTimeout(resolve, 500));
    
    setSimulationResults({
      staffing: {
        rn: { current: rn_fte, required: required_rn.toFixed(1), gap: rn_gap.toFixed(1) },
        lpn: { current: lpn_fte, required: required_lpn.toFixed(1), gap: lpn_gap.toFixed(1) },
        aide: { current: aide_fte, required: required_aide.toFixed(1), gap: aide_gap.toFixed(1) },
        total_gap: total_gap.toFixed(1)
      },
      revenue: {
        daily: daily_revenue,
        monthly: monthly_revenue,
        annual: annual_revenue
      },
      hiring: {
        total_hires: total_gap.toFixed(1),
        estimated_cost: hiring_cost
      },
      caseload: {
        average: avg_caseload.toFixed(1),
        target: 12
      },
      burnout: {
        score: burnout_score.toFixed(0),
        level: burnout_level
      }
    });
    
    setSimulationLoading(false);
  };

  const resetSimulation = () => {
    const currentAdc = summary?.staffing?.current_adc || 95;
    const staffing = summary?.staffing?.current_staff || {};
    
    setSimulationParams({
      adc: currentAdc,
      rn_fte: staffing.rn?.fte || 5,
      lpn_fte: staffing.lpn?.fte || 5,
      aide_fte: staffing.aide?.fte || 5,
      daily_rate: 185
    });
    setSimulationResults(null);
  };

  // WebSocket connection for real-time updates
  useEffect(() => {
    const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    
    const connectWebSocket = () => {
      try {
        wsRef.current = new WebSocket(`${wsUrl}/ws/operations?token=${token}`);
        
        wsRef.current.onopen = () => {
          setWsConnected(true);
          console.log('WebSocket connected');
        };
        
        wsRef.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'cascade_update') {
              // Refresh data when cascade completes
              fetchSummary();
              fetchAlerts();
              toast.info('Operations data updated', { duration: 2000 });
            }
          } catch (e) {
            console.error('WebSocket message error:', e);
          }
        };
        
        wsRef.current.onclose = () => {
          setWsConnected(false);
          // Reconnect after 5 seconds
          setTimeout(connectWebSocket, 5000);
        };
        
        wsRef.current.onerror = () => {
          setWsConnected(false);
        };
      } catch (error) {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      }
    };

    // Initial data load
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSummary(), fetchAlerts()]);
      setLoading(false);
    };
    
    loadData();
    
    // Try WebSocket connection (may not be available)
    // connectWebSocket();
    
    // Polling fallback - refresh every 30 seconds
    const pollInterval = setInterval(() => {
      fetchSummary();
      fetchAlerts();
    }, 30000);

    return () => {
      clearInterval(pollInterval);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [fetchSummary, fetchAlerts, token]);

  // Fetch analytics when tab changes or time range changes
  useEffect(() => {
    if (activeTab === 'analytics') {
      fetchAnalytics(parseInt(analyticsTimeRange));
      fetchWeeklyData();
    }
  }, [activeTab, analyticsTimeRange, fetchAnalytics, fetchWeeklyData]);

  // Fetch staff data when switching to burnout or operations tabs
  useEffect(() => {
    if (activeTab === 'burnout') {
      fetchStaffBurnout();
    }
    if (activeTab === 'operations') {
      fetchStaffPrn();
    }
    if (activeTab === 'data-quality') {
      fetchDataQuality();
    }
    if (activeTab === 'automation') {
      fetchTrustScorecard();
    }
  }, [activeTab, fetchStaffBurnout, fetchStaffPrn, fetchDataQuality]);

  // Render loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-cyan-500 mx-auto mb-4" />
          <p className="text-slate-400">Loading Operations Intelligence...</p>
        </div>
      </div>
    );
  }

  // Helper to format currency
  const formatCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value?.toFixed(0) || 0}`;
  };

  // Render KPI Card
  const KPICard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'cyan' }) => (
    <Card className="bg-slate-900/80 border-slate-700/50">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide">{title}</p>
            <p className={`text-2xl font-black text-${color}-400 mt-1`}>{value}</p>
            {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-2 rounded-lg bg-${color}-500/20`}>
            <Icon className={`h-5 w-5 text-${color}-400`} />
          </div>
        </div>
        {trend && (
          <div className="flex items-center gap-1 mt-2">
            {trend === 'up' ? (
              <ArrowUpRight className="h-3 w-3 text-emerald-400" />
            ) : trend === 'down' ? (
              <ArrowDownRight className="h-3 w-3 text-red-400" />
            ) : (
              <Minus className="h-3 w-3 text-slate-400" />
            )}
            <span className={`text-xs ${trend === 'up' ? 'text-emerald-400' : trend === 'down' ? 'text-red-400' : 'text-slate-400'}`}>
              {trendValue}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Render Overview Tab
  const renderOverview = () => {
    const staffing = summary?.staffing || {};
    const revenue = summary?.revenue_projections || summary?.revenue || {};
    const hiring = summary?.hiring_predictions || {};
    const strategic = summary?.strategic_progress || {};
    const burnout = summary?.burnout_risk || {};

    return (
      <div className="space-y-6">
        {/* Top KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPICard
            title="Current Census (ADC)"
            value={staffing?.current_adc || hiring?.current_state?.adc || '-'}
            subtitle="patients"
            icon={Users}
            color="cyan"
          />
          <KPICard
            title="Strategic Score"
            value={`${strategic?.overall_score || 0}%`}
            subtitle={`${strategic?.goals_met || 0}/${strategic?.total_goals || 0} goals met`}
            icon={Target}
            color="emerald"
          />
          <KPICard
            title="Burnout Risk"
            value={burnout?.risk_level?.toUpperCase() || 'N/A'}
            subtitle={`Score: ${burnout?.burnout_score || 0}/100`}
            icon={Flame}
            color={burnout?.risk_level === 'low' ? 'emerald' : burnout?.risk_level === 'moderate' ? 'amber' : 'red'}
          />
          <KPICard
            title="FTEs Needed"
            value={hiring?.total_hires_needed?.toFixed(0) || '0'}
            subtitle="to meet goals"
            icon={UserPlus}
            color="purple"
          />
        </div>

        {/* Alerts Section */}
        {alerts.length > 0 && (
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-white text-lg">
                <Bell className="h-5 w-5 text-amber-400" />
                Active Alerts
                <Badge className="bg-amber-500/20 text-amber-400 ml-2">{alerts.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {alerts.slice(0, 5).map((alert, idx) => (
                  <div 
                    key={alert.id || idx}
                    className={`p-3 rounded-lg border ${STATUS_COLORS[alert.priority] || STATUS_COLORS.medium}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        <span className="font-medium">{alert.message}</span>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {alert.priority?.toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Staffing Gaps */}
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Users className="h-5 w-5 text-cyan-400" />
                Staffing Analysis
              </CardTitle>
              <CardDescription>Current vs Required FTEs</CardDescription>
            </CardHeader>
            <CardContent>
              {staffing?.staffing_gaps ? (
                <div className="space-y-3">
                  {Object.entries(staffing.staffing_gaps).map(([role, data]) => {
                    const gap = data.gap || 0;
                    const pct = Math.min(100, (data.current_fte / Math.max(data.required_fte, 1)) * 100);
                    return (
                      <div key={role}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-white font-medium uppercase">{role}</span>
                          <span className={`text-sm ${gap > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                            {data.current_fte} / {data.required_fte} FTE
                            {gap > 0 && <span className="text-red-400 ml-2">(-{gap.toFixed(1)})</span>}
                          </span>
                        </div>
                        <Progress 
                          value={pct} 
                          className="h-2"
                        />
                      </div>
                    );
                  })}
                  <div className="pt-2 border-t border-slate-700/50">
                    <p className="text-sm text-slate-400">
                      Total FTE Gap: <span className="text-red-400 font-bold">{staffing.total_fte_gap?.toFixed(1) || 0}</span>
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-slate-500">No staffing data available</p>
              )}
            </CardContent>
          </Card>

          {/* Revenue Projections */}
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <DollarSign className="h-5 w-5 text-emerald-400" />
                Revenue Projections
              </CardTitle>
              <CardDescription>Based on current census</CardDescription>
            </CardHeader>
            <CardContent>
              {revenue?.projections ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                      <p className="text-xs text-slate-500">Daily</p>
                      <p className="text-xl font-bold text-emerald-400">{formatCurrency(revenue.projections.daily)}</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                      <p className="text-xs text-slate-500">Monthly</p>
                      <p className="text-xl font-bold text-emerald-400">{formatCurrency(revenue.projections.monthly)}</p>
                    </div>
                  </div>
                  <div className="bg-emerald-500/10 rounded-lg p-4 border border-emerald-500/30">
                    <p className="text-sm text-emerald-400">Annual Projection</p>
                    <p className="text-3xl font-black text-white">{formatCurrency(revenue.projections.annual)}</p>
                    {revenue.gap && (
                      <p className="text-xs text-slate-400 mt-1">
                        Target: {formatCurrency(revenue.target?.annual_revenue)} 
                        <span className="text-red-400 ml-2">
                          ({revenue.gap.growth_needed_percent?.toFixed(1)}% growth needed)
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-slate-500">No revenue data available</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Strategic Goals Progress */}
        {strategic?.progress && (
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Target className="h-5 w-5 text-purple-400" />
                Strategic Goals Progress
                <Badge className={strategic.overall_score >= 70 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}>
                  {strategic.overall_score}%
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(strategic.progress).map(([key, goal]) => (
                  <div 
                    key={key}
                    className={`p-4 rounded-lg border ${STATUS_COLORS[goal.status] || STATUS_COLORS.medium}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-slate-400">{goal.goal}</span>
                      {goal.status === 'met' || goal.status === 'on_track' ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-400" />
                      )}
                    </div>
                    <p className="text-xl font-bold text-white">{goal.current}</p>
                    <p className="text-xs text-slate-500">Target: {goal.target}</p>
                    {goal.progress_pct && (
                      <Progress value={Math.min(100, goal.progress_pct)} className="h-1 mt-2" />
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  // Render Hiring Tab
  const renderHiring = () => {
    const hiring = summary?.hiring_predictions || {};
    
    if (!hiring.hiring_needs) {
      return (
        <div className="text-center py-12">
          <UserPlus className="h-12 w-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-500">No hiring predictions available</p>
          <Button onClick={forceRecalculation} className="mt-4">
            <RefreshCw className="h-4 w-4 mr-2" />
            Generate Predictions
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPICard
            title="Current ADC"
            value={hiring.current_state?.adc || '-'}
            icon={Users}
            color="cyan"
          />
          <KPICard
            title="Target ADC"
            value={hiring.target_state?.adc || '-'}
            subtitle={`by ${hiring.target_state?.target_date}`}
            icon={Target}
            color="purple"
          />
          <KPICard
            title="Total Hires Needed"
            value={hiring.total_hires_needed?.toFixed(0) || '0'}
            subtitle="across all roles"
            icon={UserPlus}
            color="emerald"
          />
          <KPICard
            title="Est. Annual Cost"
            value={formatCurrency(hiring.budget_impact?.estimated_annual_cost)}
            icon={DollarSign}
            color="amber"
          />
        </div>

        {/* Hiring Needs by Role */}
        <Card className="bg-slate-900/80 border-slate-700/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Briefcase className="h-5 w-5 text-cyan-400" />
              Hiring Needs by Role
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-2 px-4 text-slate-400 text-sm">Role</th>
                    <th className="text-center py-2 px-4 text-slate-400 text-sm">Current FTE</th>
                    <th className="text-center py-2 px-4 text-slate-400 text-sm">Current Need</th>
                    <th className="text-center py-2 px-4 text-slate-400 text-sm">Target Need</th>
                    <th className="text-center py-2 px-4 text-slate-400 text-sm">Gap</th>
                    <th className="text-center py-2 px-4 text-slate-400 text-sm">Priority</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(hiring.hiring_needs).map(([role, needs]) => (
                    <tr key={role} className="border-b border-slate-800">
                      <td className="py-3 px-4">
                        <span className="font-medium text-white uppercase">{role.replace('_', ' ')}</span>
                      </td>
                      <td className="py-3 px-4 text-center text-slate-300">{needs.current_fte}</td>
                      <td className="py-3 px-4 text-center text-slate-300">{needs.current_need}</td>
                      <td className="py-3 px-4 text-center text-cyan-400 font-medium">{needs.target_need}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={needs.total_gap > 0 ? 'text-red-400 font-bold' : 'text-emerald-400'}>
                          {needs.total_gap > 0 ? `+${needs.total_gap}` : needs.total_gap}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Badge className={STATUS_COLORS[needs.priority]}>
                          {needs.priority}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Hiring Timeline */}
        <Card className="bg-slate-900/80 border-slate-700/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Calendar className="h-5 w-5 text-purple-400" />
              Quarterly Hiring Timeline
            </CardTitle>
            <CardDescription>
              {hiring.target_state?.weeks_remaining} weeks until target date
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {hiring.hiring_timeline?.map((quarter, idx) => (
                <div 
                  key={quarter.quarter}
                  className={`p-4 rounded-lg border ${idx === 0 ? 'bg-cyan-500/10 border-cyan-500/30' : 'bg-slate-800/50 border-slate-700/50'}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-xl font-black text-white">{quarter.quarter}</span>
                      <Badge className={idx === 0 ? 'bg-cyan-500/20 text-cyan-400' : 'bg-slate-600/50 text-slate-300'}>
                        {quarter.focus}
                      </Badge>
                    </div>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-cyan-400">{quarter.total_hires?.toFixed(0)}</span>
                      <span className="text-sm text-slate-500 ml-1">hires</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-5 gap-2">
                    {Object.entries(quarter.hires_by_role || {}).map(([role, count]) => (
                      count > 0 && (
                        <div key={role} className="bg-slate-900/50 rounded p-2 text-center">
                          <p className="text-xs text-slate-500 uppercase">{role.replace('_', ' ')}</p>
                          <p className="text-lg font-bold text-white">{count.toFixed(1)}</p>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Render Burnout Tab
  const renderBurnout = () => {
    const burnout = summary?.burnout_risk || {};
    const burnoutStyle = BURNOUT_COLORS[burnout.risk_level] || BURNOUT_COLORS.moderate;

    return (
      <div className="space-y-6">
        {/* Burnout Score */}
        <Card className={`bg-slate-900/80 border-slate-700/50`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 mb-2">Staff Burnout Risk Assessment</p>
                <div className="flex items-center gap-4">
                  <div className={`text-6xl font-black ${burnoutStyle.text}`}>
                    {burnout.burnout_score?.toFixed(0) || 0}
                  </div>
                  <div>
                    <p className="text-slate-500">out of 100</p>
                    <Badge className={`${burnoutStyle.bg}/20 ${burnoutStyle.text} mt-1`}>
                      {burnoutStyle.label}
                    </Badge>
                  </div>
                </div>
              </div>
              <div className={`p-6 rounded-full ${burnoutStyle.bg}/20`}>
                <Flame className={`h-12 w-12 ${burnoutStyle.text}`} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contributing Factors */}
        <Card className="bg-slate-900/80 border-slate-700/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <BarChart3 className="h-5 w-5 text-amber-400" />
              Contributing Factors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {burnout.factors && Object.entries(burnout.factors).map(([factor, data]) => (
                <div key={factor}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white capitalize">{factor.replace('_', ' ')}</span>
                    <span className="text-slate-400">
                      {data.contribution?.toFixed(1)} / {data.max_contribution} points
                    </span>
                  </div>
                  <div className="relative">
                    <Progress 
                      value={(data.contribution / data.max_contribution) * 100} 
                      className="h-3"
                    />
                    <span className="absolute right-0 top-0 text-xs text-slate-500">
                      Value: {data.value}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Individual Staff Burnout Risk */}
        <Card className="bg-slate-900/80 border-slate-700/50 col-span-full">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-white">
                <Users className="h-5 w-5 text-purple-400" />
                Individual Staff Burnout Risk
              </CardTitle>
              {staffBurnoutData?.summary && (
                <div className="flex items-center gap-4 text-sm">
                  <Badge className="bg-red-500/20 text-red-400">{staffBurnoutData.summary.high_risk_count} High Risk</Badge>
                  <Badge className="bg-amber-500/20 text-amber-400">{staffBurnoutData.summary.moderate_risk_count} Moderate</Badge>
                  <Badge className="bg-emerald-500/20 text-emerald-400">{staffBurnoutData.summary.low_risk_count} Low</Badge>
                </div>
              )}
            </div>
            <CardDescription className="text-slate-400">
              Burnout assessment for each staff member based on caseload, overtime, and visit intensity
            </CardDescription>
          </CardHeader>
          <CardContent>
            {staffDataLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
                <span className="ml-2 text-slate-400">Loading staff data...</span>
              </div>
            ) : staffBurnoutData?.staff?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Staff Name</th>
                      <th className="text-center py-3 px-4 text-slate-400 font-medium">Role</th>
                      <th className="text-center py-3 px-4 text-slate-400 font-medium">Team</th>
                      <th className="text-center py-3 px-4 text-slate-400 font-medium">Patients</th>
                      <th className="text-center py-3 px-4 text-slate-400 font-medium">Visits</th>
                      <th className="text-center py-3 px-4 text-slate-400 font-medium">Overtime</th>
                      <th className="text-center py-3 px-4 text-slate-400 font-medium">Burnout Score</th>
                      <th className="text-center py-3 px-4 text-slate-400 font-medium">Risk Level</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staffBurnoutData.staff.map((staff, idx) => (
                      <tr 
                        key={idx} 
                        className={`border-b border-slate-800 hover:bg-slate-800/50 ${
                          staff.risk_level === 'high' ? 'bg-red-500/5' : ''
                        }`}
                        data-testid={`staff-burnout-${idx}`}
                      >
                        <td className="py-3 px-4">
                          <span className="text-white font-medium">{staff.name}</span>
                        </td>
                        <td className="text-center py-3 px-4 text-slate-400">{staff.role}</td>
                        <td className="text-center py-3 px-4 text-slate-400">{staff.team || '-'}</td>
                        <td className="text-center py-3 px-4 text-cyan-400">{staff.metrics?.assigned_patients || '-'}</td>
                        <td className="text-center py-3 px-4 text-slate-300">{staff.metrics?.total_visits || 0}</td>
                        <td className="text-center py-3 px-4 text-amber-400">{staff.metrics?.overtime_hours || 0}h</td>
                        <td className="text-center py-3 px-4">
                          <span className={`font-bold ${
                            staff.burnout_score >= 60 ? 'text-red-400' :
                            staff.burnout_score >= 35 ? 'text-amber-400' :
                            'text-emerald-400'
                          }`}>
                            {staff.burnout_score}
                          </span>
                        </td>
                        <td className="text-center py-3 px-4">
                          <Badge className={`${
                            staff.risk_level === 'high' ? 'bg-red-500/20 text-red-400' :
                            staff.risk_level === 'moderate' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-emerald-500/20 text-emerald-400'
                          }`}>
                            {staff.risk_level}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No staff data available</p>
                <p className="text-xs mt-1">Add staff via Quick Entry to see individual burnout assessments</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recommendations */}
        <Card className="bg-slate-900/80 border-slate-700/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Shield className="h-5 w-5 text-emerald-400" />
              Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {burnout.recommendations?.map((rec, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                  <CheckCircle className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-300">{rec}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Render PRN & Caseload Tab
  const renderOperations = () => {
    const prn = summary?.prn_forecast || {};
    const caseload = summary?.caseload || {};

    return (
      <div className="space-y-6">
        {/* PRN Utilization */}
        <Card className="bg-slate-900/80 border-slate-700/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Clock className="h-5 w-5 text-amber-400" />
              PRN Utilization
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                <p className="text-xs text-slate-500">Current PRN Hours</p>
                <p className="text-2xl font-bold text-white">{prn.current_prn_hours || 0}</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                <p className="text-xs text-slate-500">PRN Percentage</p>
                <p className={`text-2xl font-bold ${(prn.prn_percentage || 0) > (prn.target_percentage || 10) ? 'text-red-400' : 'text-emerald-400'}`}>
                  {prn.prn_percentage?.toFixed(1) || 0}%
                </p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                <p className="text-xs text-slate-500">Target</p>
                <p className="text-2xl font-bold text-slate-400">{prn.target_percentage || 10}%</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                <p className="text-xs text-slate-500">Variance</p>
                <p className={`text-2xl font-bold ${(prn.variance || 0) > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                  {prn.variance > 0 ? '+' : ''}{prn.variance?.toFixed(1) || 0}%
                </p>
              </div>
            </div>
            
            {prn.recommendation && (
              <Alert className={prn.variance > 0 ? 'bg-amber-500/10 border-amber-500/30' : 'bg-emerald-500/10 border-emerald-500/30'}>
                <Info className={`h-4 w-4 ${prn.variance > 0 ? 'text-amber-400' : 'text-emerald-400'}`} />
                <AlertTitle className={prn.variance > 0 ? 'text-amber-400' : 'text-emerald-400'}>
                  Recommendation
                </AlertTitle>
                <AlertDescription className="text-slate-300">
                  {prn.recommendation}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Caseload Distribution */}
        <Card className="bg-slate-900/80 border-slate-700/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <PieChart className="h-5 w-5 text-purple-400" />
              Caseload Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                <p className="text-xs text-slate-500">Current ADC</p>
                <p className="text-2xl font-bold text-cyan-400">{caseload.current_adc || '-'}</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                <p className="text-xs text-slate-500">Avg Caseload</p>
                <p className="text-2xl font-bold text-purple-400">{caseload.average_caseload || '-'}</p>
              </div>
            </div>
            
            {caseload.distribution && (
              <div className="space-y-4">
                {Object.entries(caseload.distribution).map(([role, data]) => (
                  <div key={role} className="bg-slate-800/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium uppercase">{role}</span>
                      <Badge className={data.avg_caseload > data.target_caseload * 1.2 ? STATUS_COLORS.high : STATUS_COLORS.on_track}>
                        {data.avg_caseload} / {data.target_caseload} target
                      </Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-center text-sm">
                      <div>
                        <p className="text-slate-500">Clinicians</p>
                        <p className="text-white font-medium">{data.count}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Patients</p>
                        <p className="text-white font-medium">{data.assigned_patients}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Avg Caseload</p>
                        <p className="text-cyan-400 font-medium">{data.avg_caseload}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Staff Productivity & PRN Usage */}
        <Card className="bg-slate-900/80 border-slate-700/50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-white">
                <Users className="h-5 w-5 text-cyan-400" />
                Staff Productivity & Overtime
              </CardTitle>
              {staffPrnData?.summary && (
                <Badge className="bg-amber-500/20 text-amber-400">
                  {staffPrnData.summary.total_overtime_hours}h total overtime
                </Badge>
              )}
            </div>
            <CardDescription className="text-slate-400">
              Individual staff visits and overtime tracking
            </CardDescription>
          </CardHeader>
          <CardContent>
            {staffPrnData?.staff?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Staff Name</th>
                      <th className="text-center py-3 px-4 text-slate-400 font-medium">Role</th>
                      <th className="text-center py-3 px-4 text-slate-400 font-medium">Team</th>
                      <th className="text-center py-3 px-4 text-slate-400 font-medium">Total Visits</th>
                      <th className="text-center py-3 px-4 text-slate-400 font-medium">Overtime Hours</th>
                      <th className="text-center py-3 px-4 text-slate-400 font-medium">Records</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staffPrnData.staff.map((staff, idx) => (
                      <tr 
                        key={idx} 
                        className={`border-b border-slate-800 hover:bg-slate-800/50 ${
                          staff.overtime_hours > 10 ? 'bg-amber-500/5' : ''
                        }`}
                        data-testid={`staff-prn-${idx}`}
                      >
                        <td className="py-3 px-4">
                          <span className="text-white font-medium">{staff.name}</span>
                        </td>
                        <td className="text-center py-3 px-4 text-slate-400">{staff.role}</td>
                        <td className="text-center py-3 px-4 text-slate-400">{staff.team || '-'}</td>
                        <td className="text-center py-3 px-4 text-cyan-400 font-medium">{staff.total_visits}</td>
                        <td className="text-center py-3 px-4">
                          <span className={`font-medium ${
                            staff.overtime_hours > 10 ? 'text-red-400' :
                            staff.overtime_hours > 5 ? 'text-amber-400' :
                            'text-emerald-400'
                          }`}>
                            {staff.overtime_hours}h
                          </span>
                        </td>
                        <td className="text-center py-3 px-4 text-slate-500">{staff.records}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No staff productivity data available</p>
                <p className="text-xs mt-1">Enter productivity data via Quick Entry to see staff details</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  // ==================== RENDER DATA QUALITY TAB ====================
  const renderDataQuality = () => {
    if (dataQualityLoading) {
      return (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          <span className="ml-3 text-slate-400">Analyzing data quality...</span>
        </div>
      );
    }

    if (!dataQualityReport) {
      return (
        <div className="flex flex-col items-center justify-center py-20 text-slate-500">
          <Shield className="h-16 w-16 mb-4 opacity-50" />
          <p>Data quality report not loaded</p>
          <Button onClick={fetchDataQuality} className="mt-4">
            Run Quality Check
          </Button>
        </div>
      );
    }

    const { health_score, total_issues, issues, issue_count_by_severity } = dataQualityReport;

    return (
      <div className="space-y-6">
        {/* Health Score Header */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Health Score Card */}
          <Card className={`bg-slate-900/80 border-slate-700/50 col-span-1 ${
            health_score >= 90 ? 'border-l-4 border-l-emerald-500' :
            health_score >= 70 ? 'border-l-4 border-l-amber-500' :
            'border-l-4 border-l-red-500'
          }`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Data Health Score</p>
                  <p className={`text-4xl font-black mt-1 ${
                    health_score >= 90 ? 'text-emerald-400' :
                    health_score >= 70 ? 'text-amber-400' :
                    'text-red-400'
                  }`}>{health_score}%</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {health_score >= 90 ? 'Excellent' :
                     health_score >= 70 ? 'Good - Minor issues' :
                     'Needs Attention'}
                  </p>
                </div>
                <div className={`p-3 rounded-full ${
                  health_score >= 90 ? 'bg-emerald-500/20' :
                  health_score >= 70 ? 'bg-amber-500/20' :
                  'bg-red-500/20'
                }`}>
                  <Shield className={`h-8 w-8 ${
                    health_score >= 90 ? 'text-emerald-400' :
                    health_score >= 70 ? 'text-amber-400' :
                    'text-red-400'
                  }`} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Issue Counts */}
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardContent className="p-6">
              <p className="text-xs text-slate-500 uppercase tracking-wide">High Severity</p>
              <p className="text-3xl font-black text-red-400 mt-1">{issue_count_by_severity?.high || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Needs immediate fix</p>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardContent className="p-6">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Medium Severity</p>
              <p className="text-3xl font-black text-amber-400 mt-1">{issue_count_by_severity?.medium || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Should fix soon</p>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardContent className="p-6">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Low Severity</p>
              <p className="text-3xl font-black text-slate-400 mt-1">{issue_count_by_severity?.low || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Can fix later</p>
            </CardContent>
          </Card>
        </div>

        {/* Issues List */}
        {issues?.length > 0 ? (
          <div className="space-y-4">
            {issues.map((issue, idx) => (
              <Card 
                key={issue.id} 
                className={`bg-slate-900/80 border-slate-700/50 ${
                  issue.severity === 'high' ? 'border-l-4 border-l-red-500' :
                  issue.severity === 'medium' ? 'border-l-4 border-l-amber-500' :
                  'border-l-4 border-l-slate-500'
                }`}
                data-testid={`issue-${issue.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-white text-lg">
                      {issue.severity === 'high' ? (
                        <AlertCircle className="h-5 w-5 text-red-400" />
                      ) : issue.severity === 'medium' ? (
                        <AlertTriangle className="h-5 w-5 text-amber-400" />
                      ) : (
                        <Info className="h-5 w-5 text-slate-400" />
                      )}
                      {issue.title}
                      <Badge className={`ml-2 ${
                        issue.severity === 'high' ? 'bg-red-500/20 text-red-400' :
                        issue.severity === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-slate-500/20 text-slate-400'
                      }`}>
                        {issue.count} {issue.count === 1 ? 'issue' : 'issues'}
                      </Badge>
                    </CardTitle>
                    <Button
                      onClick={() => fixDataIssue(issue.fix_action)}
                      disabled={fixLoading[issue.fix_action]}
                      className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400"
                      data-testid={`fix-${issue.id}`}
                    >
                      {fixLoading[issue.fix_action] ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle className="h-4 w-4 mr-2" />
                      )}
                      {issue.fix_label}
                    </Button>
                  </div>
                  <CardDescription className="text-slate-400">
                    {issue.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Show issue details based on type */}
                  {issue.id === 'unlisted_staff' && issue.items?.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-700">
                            <th className="text-left py-2 px-3 text-slate-400 font-medium">Name</th>
                            <th className="text-center py-2 px-3 text-slate-400 font-medium">Discipline</th>
                            <th className="text-center py-2 px-3 text-slate-400 font-medium">Team</th>
                            <th className="text-center py-2 px-3 text-slate-400 font-medium">Records</th>
                            <th className="text-center py-2 px-3 text-slate-400 font-medium">Total Visits</th>
                            <th className="text-center py-2 px-3 text-slate-400 font-medium">Last Activity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {issue.items.slice(0, 10).map((staff, i) => (
                            <tr key={i} className="border-b border-slate-800">
                              <td className="py-2 px-3 text-white font-medium">{staff.name}</td>
                              <td className="text-center py-2 px-3 text-slate-400">{staff.discipline}</td>
                              <td className="text-center py-2 px-3 text-slate-400">{staff.team}</td>
                              <td className="text-center py-2 px-3 text-cyan-400">{staff.productivity_records}</td>
                              <td className="text-center py-2 px-3 text-slate-300">{staff.total_visits}</td>
                              <td className="text-center py-2 px-3 text-slate-500">{staff.last_activity}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {issue.items.length > 10 && (
                        <p className="text-xs text-slate-500 mt-2">
                          And {issue.items.length - 10} more...
                        </p>
                      )}
                    </div>
                  )}

                  {issue.id === 'duplicate_staff' && issue.items?.length > 0 && (
                    <div className="space-y-3">
                      {issue.items.slice(0, 5).map((dup, i) => (
                        <div key={i} className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                          <p className="text-sm text-slate-400 mb-2">{dup.match_reason}</p>
                          <div className="flex items-center gap-4 flex-wrap">
                            {dup.potential_duplicates?.map((d, j) => (
                              <div key={j} className="flex items-center gap-2 px-3 py-1 rounded bg-slate-900">
                                <span className="text-white">{d.name}</span>
                                <span className="text-xs text-slate-500">({d.role})</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {issue.id === 'missing_census' && issue.items?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {issue.items.map((date, i) => (
                        <span key={i} className="px-2 py-1 rounded bg-slate-800 text-slate-400 text-sm">
                          {date}
                        </span>
                      ))}
                    </div>
                  )}

                  {issue.id === 'incomplete_census' && issue.items?.length > 0 && (
                    <div className="space-y-2">
                      {issue.items.slice(0, 5).map((c, i) => (
                        <div key={i} className="flex items-center gap-4 text-sm">
                          <span className="text-white">{c.date}</span>
                          <span className="text-slate-500">ADC: {c.adc || 'missing'}</span>
                          <span className="text-slate-500">Admissions: {c.admissions ?? 'missing'}</span>
                          <span className="text-slate-500">Discharges: {c.discharges ?? 'missing'}</span>
                          <Badge className="bg-amber-500/20 text-amber-400">
                            {c.issues?.join(', ')}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}

                  {issue.id === 'inactive_with_activity' && issue.items?.length > 0 && (
                    <div className="space-y-2">
                      {issue.items.map((s, i) => (
                        <div key={i} className="flex items-center justify-between p-2 rounded bg-slate-800/50">
                          <span className="text-white">{s.name}</span>
                          <span className="text-slate-500">Last productivity: {s.last_productivity_date} ({s.visits} visits)</span>
                          <Badge className="bg-red-500/20 text-red-400">Status: {s.status}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardContent className="py-12">
              <div className="flex flex-col items-center justify-center text-center">
                <CheckCircle className="h-16 w-16 text-emerald-500 mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">All Clear!</h3>
                <p className="text-slate-400">No data quality issues detected. Your data is consistent.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Refresh Button */}
        <div className="flex justify-center gap-3">
          <Button
            onClick={runIntegritySync}
            disabled={syncLoading}
            className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500"
            data-testid="sync-all-data-btn"
          >
            {syncLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            {syncLoading ? 'Syncing All Data...' : 'Sync All Data'}
          </Button>
          <Button
            onClick={fetchDataQuality}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-800"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Re-run Quality Check
          </Button>
        </div>

        {/* Data Integrity Checks */}
        {integrityCheck && (
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-white">
                  <Activity className="h-5 w-5 text-cyan-400" />
                  Data Integrity ({integrityCheck.integrity_score}%)
                  <Badge className={integrityCheck.failed === 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}>
                    {integrityCheck.passed} passed / {integrityCheck.failed} failed / {integrityCheck.warnings} warnings
                  </Badge>
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {integrityCheck.checks?.map((check, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700/50" data-testid={`integrity-check-${i}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-2.5 h-2.5 rounded-full ${
                        check.status === 'pass' ? 'bg-emerald-400' :
                        check.status === 'warning' ? 'bg-amber-400' :
                        'bg-red-400'
                      }`} />
                      <div>
                        <p className="text-sm text-white font-medium">{check.name}</p>
                        <p className="text-xs text-slate-400">{check.detail}</p>
                      </div>
                    </div>
                    <Badge className={
                      check.status === 'pass' ? 'bg-emerald-500/20 text-emerald-400' :
                      check.status === 'warning' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-red-500/20 text-red-400'
                    }>
                      {check.status === 'pass' ? 'Synced' : check.status === 'warning' ? 'Needs Sync' : 'Out of Sync'}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  // ==================== RENDER ANALYTICS TAB (PHASE 3) ====================
  const renderAnalytics = () => {
    if (analyticsLoading) {
      return (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          <span className="ml-3 text-slate-400">Loading analytics...</span>
        </div>
      );
    }

    if (!analyticsData) {
      return (
        <div className="flex flex-col items-center justify-center py-20 text-slate-500">
          <History className="h-16 w-16 mb-4 opacity-50" />
          <p>No analytics data available</p>
          <Button onClick={() => fetchAnalytics(parseInt(analyticsTimeRange))} className="mt-4">
            Load Analytics
          </Button>
        </div>
      );
    }

    const { census, staffing, burnout, revenue, wow_comparison, anomalies, goal_progress } = analyticsData;

    // Format chart data
    const censusChartData = census?.data_points?.slice(-30).map(d => ({
      date: d.date?.slice(5) || '',
      adc: d.adc || 0,
      admissions: d.admissions || 0,
      discharges: d.discharges || 0
    })) || [];

    const burnoutChartData = burnout?.data_points?.slice(-30).map(d => ({
      date: d.date?.slice(5) || '',
      score: d.score || 0
    })) || [];

    const revenueChartData = revenue?.data_points?.slice(-30).map(d => ({
      date: d.date?.slice(5) || '',
      monthly: (d.monthly || 0) / 1000,
      annual: (d.annual || 0) / 1000000
    })) || [];

    const staffingChartData = staffing?.data_points?.slice(-30).map(d => ({
      date: d.date?.slice(5) || '',
      gap: d.total_fte_gap || 0
    })) || [];

    return (
      <div className="space-y-6">
        {/* View Mode Toggle and Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* View Mode Toggle */}
            <div className="flex items-center bg-slate-800 rounded-lg p-1">
              <Button
                variant={analyticsViewMode === 'weekly' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setAnalyticsViewMode('weekly')}
                className={analyticsViewMode === 'weekly' ? 'bg-blue-500 text-white' : 'text-slate-400 hover:text-white'}
                data-testid="view-weekly-btn"
              >
                <Calendar className="h-4 w-4 mr-2" />
                Weekly (Sun-Sat)
              </Button>
              <Button
                variant={analyticsViewMode === 'daily' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setAnalyticsViewMode('daily')}
                className={analyticsViewMode === 'daily' ? 'bg-blue-500 text-white' : 'text-slate-400 hover:text-white'}
                data-testid="view-daily-btn"
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                Daily Trend
              </Button>
            </div>

            <Label className="text-slate-400">|</Label>
            <Label className="text-slate-400">Time Range:</Label>
            <Select value={analyticsTimeRange} onValueChange={setAnalyticsTimeRange}>
              <SelectTrigger className="w-32 bg-slate-800 border-slate-600 text-white" data-testid="time-range-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                <SelectItem value="7">7 Days</SelectItem>
                <SelectItem value="30">30 Days</SelectItem>
                <SelectItem value="90">90 Days</SelectItem>
                <SelectItem value="365">1 Year</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { fetchAnalytics(parseInt(analyticsTimeRange)); fetchWeeklyData(); }}
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportData('census', 'csv')}
              disabled={exportLoading}
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
              data-testid="export-csv-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* WEEKLY VIEW */}
        {analyticsViewMode === 'weekly' && (
          <>
            {/* Weekly Summary Cards */}
            {weeklyData && (
              <Card className="bg-slate-900/80 border-slate-700/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Calendar className="h-5 w-5 text-blue-400" />
                    Weekly Summary (Sunday - Saturday)
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Current Week: {weeklyData.current_week?.label || 'N/A'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Current vs Previous Week Comparison */}
                  {weeklyData.current_week?.data && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                        <p className="text-xs text-slate-400 mb-1">Avg Census (ADC)</p>
                        <p className="text-2xl font-bold text-cyan-400">{weeklyData.current_week.data.avg_value || '-'}</p>
                        {weeklyData.wow_changes?.adc_change !== undefined && (
                          <div className="flex items-center gap-1 mt-1">
                            {weeklyData.wow_changes.adc_change > 0 ? (
                              <ArrowUpRight className="h-4 w-4 text-emerald-400" />
                            ) : weeklyData.wow_changes.adc_change < 0 ? (
                              <ArrowDownRight className="h-4 w-4 text-red-400" />
                            ) : (
                              <Minus className="h-4 w-4 text-slate-400" />
                            )}
                            <span className={`text-sm ${weeklyData.wow_changes.adc_change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {weeklyData.wow_changes.adc_change > 0 ? '+' : ''}{weeklyData.wow_changes.adc_change} vs last week
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                        <p className="text-xs text-slate-400 mb-1">Admissions This Week</p>
                        <p className="text-2xl font-bold text-emerald-400">{weeklyData.current_week.data.total_admissions || 0}</p>
                        {weeklyData.wow_changes?.admissions_change !== undefined && (
                          <span className={`text-sm ${weeklyData.wow_changes.admissions_change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {weeklyData.wow_changes.admissions_change > 0 ? '+' : ''}{weeklyData.wow_changes.admissions_change} vs last week
                          </span>
                        )}
                      </div>
                      <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                        <p className="text-xs text-slate-400 mb-1">Discharges This Week</p>
                        <p className="text-2xl font-bold text-amber-400">{weeklyData.current_week.data.total_discharges || 0}</p>
                        {weeklyData.wow_changes?.discharges_change !== undefined && (
                          <span className={`text-sm ${weeklyData.wow_changes.discharges_change <= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {weeklyData.wow_changes.discharges_change > 0 ? '+' : ''}{weeklyData.wow_changes.discharges_change} vs last week
                          </span>
                        )}
                      </div>
                      <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/30">
                        <p className="text-xs text-slate-400 mb-1">Net Change</p>
                        <p className={`text-2xl font-bold ${(weeklyData.current_week.data.net_change || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {weeklyData.current_week.data.net_change > 0 ? '+' : ''}{weeklyData.current_week.data.net_change || 0}
                        </p>
                        <span className="text-xs text-slate-500">Admissions - Discharges - Deaths</span>
                      </div>
                    </div>
                  )}

                  {/* Weekly History Table */}
                  {weeklyData.all_weeks?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-700">
                            <th className="text-left py-3 px-4 text-slate-400 font-medium">Week</th>
                            <th className="text-center py-3 px-4 text-slate-400 font-medium">Avg ADC</th>
                            <th className="text-center py-3 px-4 text-slate-400 font-medium">Min</th>
                            <th className="text-center py-3 px-4 text-slate-400 font-medium">Max</th>
                            <th className="text-center py-3 px-4 text-slate-400 font-medium">Admissions</th>
                            <th className="text-center py-3 px-4 text-slate-400 font-medium">Discharges</th>
                            <th className="text-center py-3 px-4 text-slate-400 font-medium">Deaths</th>
                            <th className="text-center py-3 px-4 text-slate-400 font-medium">Net</th>
                            <th className="text-center py-3 px-4 text-slate-400 font-medium">Days</th>
                          </tr>
                        </thead>
                        <tbody>
                          {weeklyData.all_weeks.slice().reverse().map((week, idx) => (
                            <tr 
                              key={week.week_key} 
                              className={`border-b border-slate-800 hover:bg-slate-800/50 ${idx === 0 ? 'bg-blue-500/10' : ''}`}
                              data-testid={`week-row-${week.week_key}`}
                            >
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                  {idx === 0 && <Badge className="bg-blue-500/20 text-blue-400 text-xs">Current</Badge>}
                                  <span className="text-white font-medium">{week.week_label}</span>
                                </div>
                              </td>
                              <td className="text-center py-3 px-4 text-cyan-400 font-bold">{week.avg_value || '-'}</td>
                              <td className="text-center py-3 px-4 text-slate-400">{week.min_value || '-'}</td>
                              <td className="text-center py-3 px-4 text-slate-400">{week.max_value || '-'}</td>
                              <td className="text-center py-3 px-4 text-emerald-400">{week.total_admissions || 0}</td>
                              <td className="text-center py-3 px-4 text-amber-400">{week.total_discharges || 0}</td>
                              <td className="text-center py-3 px-4 text-red-400">{week.total_deaths || 0}</td>
                              <td className={`text-center py-3 px-4 font-medium ${(week.net_change || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                {week.net_change > 0 ? '+' : ''}{week.net_change || 0}
                              </td>
                              <td className="text-center py-3 px-4 text-slate-500">{week.days_with_data || 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No weekly data available</p>
                      <p className="text-xs mt-1">Enter census data via Quick Entry to see weekly summaries</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Weekly Trend Chart */}
            {weeklyData?.all_weeks?.length > 0 && (
              <Card className="bg-slate-900/80 border-slate-700/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white text-base">
                    <LineChart className="h-5 w-5 text-cyan-400" />
                    Weekly ADC Trend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={weeklyData.all_weeks.map(w => ({
                        week: w.week_label.split(' - ')[0],
                        adc: w.avg_value || 0,
                        admissions: w.total_admissions || 0,
                        discharges: w.total_discharges || 0
                      }))}>
                        <defs>
                          <linearGradient id="weeklyAdcGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="week" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                        <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                          labelStyle={{ color: '#f8fafc' }}
                        />
                        <Legend />
                        <Area type="monotone" dataKey="adc" name="Avg ADC" stroke="#06b6d4" fill="url(#weeklyAdcGradient)" strokeWidth={2} />
                        <Line type="monotone" dataKey="admissions" name="Admissions" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981' }} />
                        <Line type="monotone" dataKey="discharges" name="Discharges" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b' }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* DAILY VIEW (Original Charts) */}
        {analyticsViewMode === 'daily' && (
          <>
            {/* Week-over-Week Comparison Cards */}
            {wow_comparison?.comparisons && (
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <TrendingUp className="h-5 w-5 text-blue-400" />
                Week-over-Week Comparison
              </CardTitle>
              <CardDescription className="text-slate-400">
                {wow_comparison.last_week} vs {wow_comparison.this_week}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {Object.entries(wow_comparison.comparisons).map(([key, comp]) => (
                  <div 
                    key={key} 
                    className={`p-4 rounded-lg border ${
                      comp.is_improvement 
                        ? 'bg-emerald-500/10 border-emerald-500/30' 
                        : comp.delta === 0 
                          ? 'bg-slate-800/50 border-slate-700' 
                          : 'bg-red-500/10 border-red-500/30'
                    }`}
                    data-testid={`wow-${key}`}
                  >
                    <p className="text-xs text-slate-400 mb-1">{comp.label}</p>
                    <p className="text-xl font-bold text-white">
                      {comp.unit === 'USD' ? formatCurrency(comp.current) : comp.current}
                    </p>
                    <div className="flex items-center gap-1 mt-1">
                      {comp.trend === 'up' ? (
                        <ArrowUpRight className={`h-4 w-4 ${comp.is_improvement ? 'text-emerald-400' : 'text-red-400'}`} />
                      ) : comp.trend === 'down' ? (
                        <ArrowDownRight className={`h-4 w-4 ${comp.is_improvement ? 'text-emerald-400' : 'text-red-400'}`} />
                      ) : (
                        <Minus className="h-4 w-4 text-slate-400" />
                      )}
                      <span className={`text-sm ${comp.is_improvement ? 'text-emerald-400' : comp.delta === 0 ? 'text-slate-400' : 'text-red-400'}`}>
                        {comp.delta > 0 ? '+' : ''}{comp.delta_percent}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Trend Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Census Trend Chart */}
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-white text-base">
                  <Users className="h-5 w-5 text-cyan-400" />
                  Census (ADC) Trend
                </CardTitle>
                <Badge className={`${
                  census?.summary?.trend?.direction === 'up' ? 'bg-emerald-500/20 text-emerald-400' :
                  census?.summary?.trend?.direction === 'down' ? 'bg-red-500/20 text-red-400' :
                  'bg-slate-500/20 text-slate-400'
                }`}>
                  {census?.summary?.trend?.direction === 'up' ? '↑' : census?.summary?.trend?.direction === 'down' ? '↓' : '→'}
                  {' '}{census?.summary?.trend?.weekly_change > 0 ? '+' : ''}{census?.summary?.trend?.weekly_change}/week
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={censusChartData}>
                    <defs>
                      <linearGradient id="censusGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                      labelStyle={{ color: '#f8fafc' }}
                    />
                    <Area type="monotone" dataKey="adc" stroke="#06b6d4" fill="url(#censusGradient)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-4 text-center">
                <div className="bg-slate-800/50 rounded p-2">
                  <p className="text-xs text-slate-500">Current</p>
                  <p className="text-lg font-bold text-cyan-400">{census?.summary?.current || '-'}</p>
                </div>
                <div className="bg-slate-800/50 rounded p-2">
                  <p className="text-xs text-slate-500">Average</p>
                  <p className="text-lg font-bold text-white">{census?.summary?.average || '-'}</p>
                </div>
                <div className="bg-slate-800/50 rounded p-2">
                  <p className="text-xs text-slate-500">Min</p>
                  <p className="text-lg font-bold text-slate-400">{census?.summary?.min || '-'}</p>
                </div>
                <div className="bg-slate-800/50 rounded p-2">
                  <p className="text-xs text-slate-500">Max</p>
                  <p className="text-lg font-bold text-slate-400">{census?.summary?.max || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Burnout Risk Trend */}
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-white text-base">
                  <Flame className="h-5 w-5 text-amber-400" />
                  Burnout Risk Trend
                </CardTitle>
                <Badge className={`${
                  burnout?.summary?.trend?.direction === 'down' ? 'bg-emerald-500/20 text-emerald-400' :
                  burnout?.summary?.trend?.direction === 'up' ? 'bg-red-500/20 text-red-400' :
                  'bg-slate-500/20 text-slate-400'
                }`}>
                  {burnout?.summary?.trend?.direction === 'up' ? '↑' : burnout?.summary?.trend?.direction === 'down' ? '↓' : '→'}
                  {' '}{burnout?.summary?.trend?.weekly_change}/week
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={burnoutChartData}>
                    <defs>
                      <linearGradient id="burnoutGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                    <YAxis domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 10 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                      labelStyle={{ color: '#f8fafc' }}
                    />
                    <Area type="monotone" dataKey="score" stroke="#f59e0b" fill="url(#burnoutGradient)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-4 text-center">
                <div className="bg-slate-800/50 rounded p-2">
                  <p className="text-xs text-slate-500">Current</p>
                  <p className={`text-lg font-bold ${
                    (burnout?.summary?.current || 0) >= 50 ? 'text-red-400' : 
                    (burnout?.summary?.current || 0) >= 30 ? 'text-amber-400' : 'text-emerald-400'
                  }`}>{burnout?.summary?.current || '-'}</p>
                </div>
                <div className="bg-slate-800/50 rounded p-2">
                  <p className="text-xs text-slate-500">Average</p>
                  <p className="text-lg font-bold text-white">{burnout?.summary?.average || '-'}</p>
                </div>
                <div className="bg-slate-800/50 rounded p-2">
                  <p className="text-xs text-slate-500">Min</p>
                  <p className="text-lg font-bold text-emerald-400">{burnout?.summary?.min || '-'}</p>
                </div>
                <div className="bg-slate-800/50 rounded p-2">
                  <p className="text-xs text-slate-500">Max</p>
                  <p className="text-lg font-bold text-red-400">{burnout?.summary?.max || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Staffing Gap Trend */}
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-white text-base">
                  <UserPlus className="h-5 w-5 text-purple-400" />
                  Staffing Gap Trend
                </CardTitle>
                <Badge className={`${
                  staffing?.summary?.trend?.direction === 'down' ? 'bg-emerald-500/20 text-emerald-400' :
                  staffing?.summary?.trend?.direction === 'up' ? 'bg-red-500/20 text-red-400' :
                  'bg-slate-500/20 text-slate-400'
                }`}>
                  {staffing?.summary?.trend?.direction === 'up' ? '↑' : staffing?.summary?.trend?.direction === 'down' ? '↓' : '→'}
                  {' '}{staffing?.summary?.trend?.weekly_change > 0 ? '+' : ''}{staffing?.summary?.trend?.weekly_change} FTE/week
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={staffingChartData}>
                    <defs>
                      <linearGradient id="staffingGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#a855f7" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#a855f7" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                      labelStyle={{ color: '#f8fafc' }}
                    />
                    <Area type="monotone" dataKey="gap" stroke="#a855f7" fill="url(#staffingGradient)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-4 text-center">
                <div className="bg-slate-800/50 rounded p-2">
                  <p className="text-xs text-slate-500">Current</p>
                  <p className="text-lg font-bold text-purple-400">{staffing?.summary?.current || '-'} FTE</p>
                </div>
                <div className="bg-slate-800/50 rounded p-2">
                  <p className="text-xs text-slate-500">Average</p>
                  <p className="text-lg font-bold text-white">{staffing?.summary?.average || '-'}</p>
                </div>
                <div className="bg-slate-800/50 rounded p-2">
                  <p className="text-xs text-slate-500">Min</p>
                  <p className="text-lg font-bold text-emerald-400">{staffing?.summary?.min || '-'}</p>
                </div>
                <div className="bg-slate-800/50 rounded p-2">
                  <p className="text-xs text-slate-500">Max</p>
                  <p className="text-lg font-bold text-red-400">{staffing?.summary?.max || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Revenue Trend */}
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-white text-base">
                  <DollarSign className="h-5 w-5 text-emerald-400" />
                  Revenue Projection Trend
                </CardTitle>
                <Badge className={`${
                  revenue?.summary?.trend?.direction === 'up' ? 'bg-emerald-500/20 text-emerald-400' :
                  revenue?.summary?.trend?.direction === 'down' ? 'bg-red-500/20 text-red-400' :
                  'bg-slate-500/20 text-slate-400'
                }`}>
                  {revenue?.summary?.trend?.direction === 'up' ? '↑' : revenue?.summary?.trend?.direction === 'down' ? '↓' : '→'}
                  {' '}{revenue?.summary?.trend?.percent_change}%
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={revenueChartData}>
                    <defs>
                      <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={(v) => `$${v}M`} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                      labelStyle={{ color: '#f8fafc' }}
                      formatter={(value, name) => [name === 'annual' ? `$${value.toFixed(1)}M` : `$${value.toFixed(0)}K`, name === 'annual' ? 'Annual' : 'Monthly']}
                    />
                    <Area type="monotone" dataKey="annual" stroke="#10b981" fill="url(#revenueGradient)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                <div className="bg-slate-800/50 rounded p-2">
                  <p className="text-xs text-slate-500">Current Annual</p>
                  <p className="text-lg font-bold text-emerald-400">{formatCurrency(revenue?.summary?.current || 0)}</p>
                </div>
                <div className="bg-slate-800/50 rounded p-2">
                  <p className="text-xs text-slate-500">Average</p>
                  <p className="text-lg font-bold text-white">{formatCurrency(revenue?.summary?.average || 0)}</p>
                </div>
                <div className="bg-slate-800/50 rounded p-2">
                  <p className="text-xs text-slate-500">Peak</p>
                  <p className="text-lg font-bold text-cyan-400">{formatCurrency(revenue?.summary?.max || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Anomalies and Goal Progress Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Anomaly Detection */}
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <AlertTriangle className="h-5 w-5 text-amber-400" />
                Anomaly Detection
                {anomalies?.total_detected > 0 && (
                  <Badge className="bg-amber-500/20 text-amber-400 ml-2">
                    {anomalies.total_detected} detected
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="text-slate-400">
                Unusual patterns flagged by AI analysis
              </CardDescription>
            </CardHeader>
            <CardContent>
              {anomalies?.anomalies?.length > 0 ? (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {anomalies.anomalies.slice(0, 5).map((anomaly, i) => (
                    <div 
                      key={i}
                      className={`p-3 rounded-lg border ${
                        anomaly.severity === 'high' 
                          ? 'bg-red-500/10 border-red-500/30' 
                          : 'bg-amber-500/10 border-amber-500/30'
                      }`}
                      data-testid={`anomaly-${i}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-white">{anomaly.metric}</span>
                        <Badge className={anomaly.severity === 'high' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}>
                          {anomaly.type} • {anomaly.severity}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-400">{anomaly.description}</p>
                      <div className="flex items-center justify-between mt-2 text-xs">
                        <span className="text-slate-500">{anomaly.date}</span>
                        <span className="text-slate-400">Expected: {anomaly.expected_range}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-slate-500">
                  <CheckCircle className="h-12 w-12 mb-3 text-emerald-500 opacity-50" />
                  <p>No anomalies detected</p>
                  <p className="text-xs mt-1">Operations running within expected ranges</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Goal Progress Timeline */}
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Target className="h-5 w-5 text-cyan-400" />
                Strategic Goal Progress
              </CardTitle>
              <CardDescription className="text-slate-400">
                ADC target: {goal_progress?.current_state?.target_adc || 120} by {goal_progress?.current_state?.target_date || '2026-12-31'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Current Progress */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400">Current Progress</span>
                  <span className="text-sm font-medium text-cyan-400">
                    {goal_progress?.current_state?.progress_percent || 0}%
                  </span>
                </div>
                <Progress 
                  value={goal_progress?.current_state?.progress_percent || 0} 
                  className="h-3 bg-slate-800"
                />
                <div className="flex items-center justify-between mt-1 text-xs text-slate-500">
                  <span>ADC: {goal_progress?.current_state?.adc || 0}</span>
                  <span>Target: {goal_progress?.current_state?.target_adc || 120}</span>
                </div>
              </div>

              {/* Projection Status */}
              <div className={`p-3 rounded-lg border mb-4 ${
                goal_progress?.projection?.on_track 
                  ? 'bg-emerald-500/10 border-emerald-500/30' 
                  : 'bg-amber-500/10 border-amber-500/30'
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  {goal_progress?.projection?.on_track ? (
                    <CheckCircle className="h-4 w-4 text-emerald-400" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-amber-400" />
                  )}
                  <span className={`font-medium ${goal_progress?.projection?.on_track ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {goal_progress?.projection?.on_track ? 'On Track' : 'Behind Schedule'}
                  </span>
                </div>
                <p className="text-xs text-slate-400">
                  Trend: {goal_progress?.projection?.trend_direction || 'flat'} 
                  ({goal_progress?.projection?.weekly_change > 0 ? '+' : ''}{goal_progress?.projection?.weekly_change || 0}/week)
                </p>
                {goal_progress?.projection?.projected_achievement_date && (
                  <p className="text-xs text-slate-400 mt-1">
                    Projected completion: {goal_progress?.projection?.projected_achievement_date}
                  </p>
                )}
              </div>

              {/* Milestones */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-slate-400 mb-2">MILESTONES</p>
                <div className="flex items-center gap-2 flex-wrap">
                  {goal_progress?.milestones?.map((milestone, i) => (
                    <div 
                      key={i}
                      className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                        milestone.achieved 
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                          : 'bg-slate-800 text-slate-500 border border-slate-700'
                      }`}
                    >
                      {milestone.achieved ? (
                        <CheckCircle className="h-3 w-3" />
                      ) : (
                        <Flag className="h-3 w-3" />
                      )}
                      <span>{milestone.label} ({milestone.target_adc})</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-950 p-6" data-testid="operations-command-center">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-600">
            <Brain className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-black text-white">Operations Command Center</h1>
            <p className="text-slate-400">Autonomous Operations Intelligence</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Connection Status */}
          <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800/50">
            <div className={`h-2 w-2 rounded-full ${wsConnected ? 'bg-emerald-500 animate-pulse' : 'bg-slate-500'}`} />
            <span className="text-xs text-slate-400">
              {wsConnected ? 'Live' : 'Polling'}
            </span>
          </div>
          
          {/* Last Update */}
          {lastUpdate && (
            <span className="text-xs text-slate-500">
              Updated: {lastUpdate.toLocaleTimeString()}
            </span>
          )}
          
          {/* What-If Simulation Button */}
          <Button
            onClick={() => { resetSimulation(); setShowSimulation(true); }}
            variant="outline"
            className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
            data-testid="simulation-btn"
          >
            <FlaskConical className="h-4 w-4 mr-2" />
            What-If Simulation
          </Button>
          
          {/* Data Entry Button */}
          <Button
            onClick={() => setShowDataEntry(true)}
            className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400"
            data-testid="data-entry-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Quick Entry
          </Button>
          
          {/* Refresh Button */}
          <Button
            onClick={forceRecalculation}
            disabled={refreshing}
            variant="outline"
            className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
            data-testid="refresh-btn"
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Recalculate All
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-900/80 border border-slate-700/50">
          <TabsTrigger value="overview" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
            <Activity className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="analytics" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400" data-testid="analytics-tab">
            <History className="h-4 w-4 mr-2" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="data-quality" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400" data-testid="data-quality-tab">
            <Shield className="h-4 w-4 mr-2" />
            Data Quality
          </TabsTrigger>
          <TabsTrigger value="hiring" className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400">
            <UserPlus className="h-4 w-4 mr-2" />
            Hiring Predictions
          </TabsTrigger>
          <TabsTrigger value="burnout" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400">
            <Flame className="h-4 w-4 mr-2" />
            Burnout Risk
          </TabsTrigger>
          <TabsTrigger value="operations" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400">
            <BarChart3 className="h-4 w-4 mr-2" />
            PRN & Caseload
          </TabsTrigger>
          <TabsTrigger value="automation" className="data-[state=active]:bg-violet-500/20 data-[state=active]:text-violet-400" data-testid="automation-tab">
            <Zap className="h-4 w-4 mr-2" />
            Automation & Trust
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          {renderOverview()}
        </TabsContent>

        <TabsContent value="analytics" className="mt-6" data-testid="analytics-content">
          {renderAnalytics()}
        </TabsContent>

        <TabsContent value="data-quality" className="mt-6" data-testid="data-quality-content">
          {renderDataQuality()}
        </TabsContent>
        
        <TabsContent value="hiring" className="mt-6">
          {renderHiring()}
        </TabsContent>
        
        <TabsContent value="burnout" className="mt-6">
          {renderBurnout()}
        </TabsContent>
        
        <TabsContent value="operations" className="mt-6">
          {renderOperations()}
        </TabsContent>
        
        {/* Automation & Trust Tab */}
        <TabsContent value="automation" className="mt-6 space-y-6" data-testid="automation-content">
          {/* Trust Scorecard Header */}
          <Card className="bg-slate-900/80 border-slate-700/50 overflow-hidden">
            <div className={`p-6 border-l-4 ${
              trustScorecard?.overall_trust === 'excellent' ? 'border-emerald-500 bg-emerald-500/5' :
              trustScorecard?.overall_trust === 'good' ? 'border-blue-500 bg-blue-500/5' :
              trustScorecard?.overall_trust === 'fair' ? 'border-amber-500 bg-amber-500/5' :
              'border-violet-500 bg-violet-500/5'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <Shield className="h-6 w-6 text-violet-400" />
                    Operational Trust Scorecard
                  </h3>
                  <p className="text-sm text-slate-400 mt-1">
                    As of week {trustScorecard?.as_of_week || '—'} · {trustScorecard?.weeks_of_scored_history || 0} weeks of scored history
                  </p>
                </div>
                <div className="text-right">
                  <div className={`text-3xl font-black uppercase ${
                    trustScorecard?.overall_trust === 'excellent' ? 'text-emerald-400' :
                    trustScorecard?.overall_trust === 'good' ? 'text-blue-400' :
                    trustScorecard?.overall_trust === 'fair' ? 'text-amber-400' :
                    'text-violet-400'
                  }`}>
                    {trustScorecard?.overall_trust || 'Loading...'}
                  </div>
                  {trustScorecard?.overall_avg_mape_pct != null && (
                    <div className="text-sm text-slate-400">Avg MAPE: {trustScorecard.overall_avg_mape_pct}%</div>
                  )}
                </div>
              </div>
              <p className="text-sm text-slate-300 mt-3 italic">{trustScorecard?.interpretation || ''}</p>
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Forecast Accuracy by Horizon */}
            <Card className="bg-slate-900/80 border-slate-700/50">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-white">
                  <Target className="h-5 w-5 text-blue-400" />
                  Census Forecast Accuracy (ADC)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {trustScorecard?.census_projection?.adc && Object.keys(trustScorecard.census_projection.adc).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(trustScorecard.census_projection.adc).map(([horizon, data]) => (
                      <div key={horizon} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
                        <div>
                          <p className="text-white font-medium">{horizon} ahead</p>
                          <p className="text-xs text-slate-400">{data.n_scored} scored · {data.proven ? 'Proven' : 'Building'}</p>
                        </div>
                        <div className="text-right">
                          <Badge className={
                            data.trust === 'excellent' ? 'bg-emerald-500/20 text-emerald-400' :
                            data.trust === 'good' ? 'bg-blue-500/20 text-blue-400' :
                            data.trust === 'fair' ? 'bg-amber-500/20 text-amber-400' :
                            data.trust === 'unproven' ? 'bg-slate-500/20 text-slate-400' :
                            'bg-red-500/20 text-red-400'
                          }>
                            {data.mape_pct != null ? `${data.mape_pct}% MAPE` : 'No data'}
                          </Badge>
                          {data.mae != null && <p className="text-xs text-slate-500 mt-1">MAE: {data.mae}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Timer className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p>No forecast accuracy data yet</p>
                    <p className="text-xs mt-1">Enter weekly census to build track record</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Hit Rates */}
            <Card className="bg-slate-900/80 border-slate-700/50">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-white">
                  <Bot className="h-5 w-5 text-amber-400" />
                  Prediction Hit Rates
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Burnout */}
                <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700/50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium flex items-center gap-2">
                      <Flame className="h-4 w-4 text-amber-400" />
                      Burnout Flags
                    </span>
                    <Badge className={trustScorecard?.burnout_flags?.proven ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}>
                      {trustScorecard?.burnout_flags?.proven ? 'Proven' : 'Building'}
                    </Badge>
                  </div>
                  {trustScorecard?.burnout_flags?.precision != null ? (
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-slate-400">Precision:</span> <span className="text-white font-bold">{(trustScorecard.burnout_flags.precision * 100).toFixed(0)}%</span></div>
                      <div><span className="text-slate-400">Recall:</span> <span className="text-white font-bold">{(trustScorecard.burnout_flags.recall * 100).toFixed(0)}%</span></div>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500">Not enough scored data yet</p>
                  )}
                </div>
                {/* Hiring */}
                <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700/50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium flex items-center gap-2">
                      <UserPlus className="h-4 w-4 text-purple-400" />
                      Hiring Predictions
                    </span>
                    <Badge className={trustScorecard?.hiring_predictions?.proven ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}>
                      {trustScorecard?.hiring_predictions?.proven ? 'Proven' : 'Building'}
                    </Badge>
                  </div>
                  {trustScorecard?.hiring_predictions?.precision != null ? (
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-slate-400">Precision:</span> <span className="text-white font-bold">{(trustScorecard.hiring_predictions.precision * 100).toFixed(0)}%</span></div>
                      <div><span className="text-slate-400">Recall:</span> <span className="text-white font-bold">{(trustScorecard.hiring_predictions.recall * 100).toFixed(0)}%</span></div>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500">Not enough scored data yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Automation Status */}
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-white">
                  <Zap className="h-5 w-5 text-violet-400" />
                  Automated Workflows
                </CardTitle>
                <Button onClick={fetchTrustScorecard} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                  <RefreshCw className={`h-4 w-4 mr-2 ${trustLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                    <h4 className="text-white font-medium">Post-Census Loop</h4>
                  </div>
                  <p className="text-xs text-slate-400 mb-2">On every census entry:</p>
                  <ul className="text-xs text-emerald-300 space-y-1">
                    <li>1. Cascade 7 calculations</li>
                    <li>2. Generate 8-week forecast</li>
                    <li>3. Score prior predictions</li>
                    <li>4. Update trust scorecard</li>
                  </ul>
                  <Badge className="mt-3 bg-emerald-500/20 text-emerald-400">Active</Badge>
                </div>

                <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Timer className="h-5 w-5 text-blue-400" />
                    <h4 className="text-white font-medium">Hourly Integrity Sync</h4>
                  </div>
                  <p className="text-xs text-slate-400 mb-2">Every hour at :30:</p>
                  <ul className="text-xs text-blue-300 space-y-1">
                    <li>• Census → Program Overview</li>
                    <li>• Census → Admissions/Discharges</li>
                    <li>• Weekly data reconciliation</li>
                  </ul>
                  <Badge className="mt-3 bg-blue-500/20 text-blue-400">Scheduled</Badge>
                </div>

                <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="h-5 w-5 text-purple-400" />
                    <h4 className="text-white font-medium">Daily Audit (2 AM)</h4>
                  </div>
                  <p className="text-xs text-slate-400 mb-2">Every night at 2:00 AM:</p>
                  <ul className="text-xs text-purple-300 space-y-1">
                    <li>• Full integrity sync</li>
                    <li>• Data validation audit</li>
                    <li>• Trust scorecard refresh</li>
                  </ul>
                  <Badge className="mt-3 bg-purple-500/20 text-purple-400">Scheduled</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Governed Coefficients */}
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-white">
                <Settings className="h-5 w-5 text-slate-400" />
                Governed Operational Coefficients
                <Badge variant="outline" className="border-slate-600 text-slate-400 text-xs ml-2">17 coefficients</Badge>
              </CardTitle>
              <p className="text-xs text-slate-500 mt-1">Every formula coefficient documented — no magic numbers</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { label: 'Burnout: Caseload Divisor', value: '12', unit: 'patients/RNCM', desc: 'Optimal caseload per RNCM' },
                  { label: 'Burnout: PRN Divisor', value: '10', unit: '%', desc: 'PRN above 10% = understaffing' },
                  { label: 'Burnout: Overtime Divisor', value: '5', unit: 'hrs/wk', desc: '5+ OT hours = overwork signal' },
                  { label: 'Forecast: Min Weeks', value: '4', unit: 'weeks', desc: 'Minimum history for forecasting' },
                  { label: 'Staffing: Target Caseload', value: '12', unit: 'patients/RNCM', desc: 'NHPCO benchmark' },
                  { label: 'Hiring: Lead Time', value: '8', unit: 'weeks', desc: 'Avg time to productive start' },
                ].map((coeff, i) => (
                  <div key={i} className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-slate-400">{coeff.label}</span>
                      <Badge className="bg-violet-500/20 text-violet-400">{coeff.value} {coeff.unit}</Badge>
                    </div>
                    <p className="text-xs text-slate-500">{coeff.desc}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Data Entry Dialog */}
      <Dialog open={showDataEntry} onOpenChange={setShowDataEntry}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileInput className="h-5 w-5 text-emerald-400" />
              Quick Data Entry
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Enter new data. Changes will automatically trigger cascade recalculations.
            </DialogDescription>
          </DialogHeader>

          {/* Data Type Selector */}
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Data Type</Label>
              <Select value={dataEntryType} onValueChange={setDataEntryType}>
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white mt-1" data-testid="data-type-select">
                  <SelectValue placeholder="Select data type" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  <SelectItem value="census">Census / ADC</SelectItem>
                  <SelectItem value="staff">Staff Roster</SelectItem>
                  <SelectItem value="productivity">Productivity</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Census Entry Form */}
            {dataEntryType === 'census' && (
              <div className="space-y-4 p-4 rounded-lg bg-slate-800/50 border border-slate-700" data-testid="census-form">
                <h4 className="text-sm font-medium text-cyan-400 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Census Data Entry
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-400 text-sm">Date</Label>
                    <Input
                      type="date"
                      value={censusForm.date}
                      onChange={(e) => setCensusForm({...censusForm, date: e.target.value})}
                      className="bg-slate-900 border-slate-600 text-white mt-1"
                      data-testid="census-date"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">ADC (Average Daily Census) *</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 95"
                      value={censusForm.adc}
                      onChange={(e) => setCensusForm({...censusForm, adc: e.target.value})}
                      className="bg-slate-900 border-slate-600 text-white mt-1"
                      data-testid="census-adc"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">Admissions</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={censusForm.admissions}
                      onChange={(e) => setCensusForm({...censusForm, admissions: e.target.value})}
                      className="bg-slate-900 border-slate-600 text-white mt-1"
                      data-testid="census-admissions"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">Discharges</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={censusForm.discharges}
                      onChange={(e) => setCensusForm({...censusForm, discharges: e.target.value})}
                      className="bg-slate-900 border-slate-600 text-white mt-1"
                      data-testid="census-discharges"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">Deaths</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={censusForm.deaths}
                      onChange={(e) => setCensusForm({...censusForm, deaths: e.target.value})}
                      className="bg-slate-900 border-slate-600 text-white mt-1"
                      data-testid="census-deaths"
                    />
                  </div>
                </div>
                <Alert className="bg-cyan-500/10 border-cyan-500/30">
                  <Info className="h-4 w-4 text-cyan-400" />
                  <AlertDescription className="text-slate-300 text-sm">
                    Saving census will auto-update: Staffing Requirements, Revenue Projections, Hiring Predictions, Strategic Goals
                  </AlertDescription>
                </Alert>
              </div>
            )}

            {/* Staff Entry Form */}
            {dataEntryType === 'staff' && (
              <div className="space-y-4 p-4 rounded-lg bg-slate-800/50 border border-slate-700" data-testid="staff-form">
                <h4 className="text-sm font-medium text-purple-400 flex items-center gap-2">
                  <UserPlus className="h-4 w-4" />
                  Add Staff Member
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label className="text-slate-400 text-sm">Name *</Label>
                    <Input
                      type="text"
                      placeholder="Full Name"
                      value={staffForm.name}
                      onChange={(e) => setStaffForm({...staffForm, name: e.target.value})}
                      className="bg-slate-900 border-slate-600 text-white mt-1"
                      data-testid="staff-name"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">Role</Label>
                    <Select value={staffForm.role} onValueChange={(v) => setStaffForm({...staffForm, role: v})}>
                      <SelectTrigger className="bg-slate-900 border-slate-600 text-white mt-1" data-testid="staff-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-600">
                        <SelectItem value="rn">RN (Registered Nurse)</SelectItem>
                        <SelectItem value="lpn">LPN (Licensed Practical Nurse)</SelectItem>
                        <SelectItem value="aide">Home Health Aide</SelectItem>
                        <SelectItem value="social_worker">Social Worker</SelectItem>
                        <SelectItem value="chaplain">Chaplain</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">FTE</Label>
                    <Select value={staffForm.fte} onValueChange={(v) => setStaffForm({...staffForm, fte: v})}>
                      <SelectTrigger className="bg-slate-900 border-slate-600 text-white mt-1" data-testid="staff-fte">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-600">
                        <SelectItem value="1.0">1.0 (Full Time)</SelectItem>
                        <SelectItem value="0.8">0.8</SelectItem>
                        <SelectItem value="0.6">0.6</SelectItem>
                        <SelectItem value="0.5">0.5 (Half Time)</SelectItem>
                        <SelectItem value="0.25">0.25 (PRN)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-slate-400 text-sm">Team (optional)</Label>
                    <Input
                      type="text"
                      placeholder="e.g., Team Alpha"
                      value={staffForm.team}
                      onChange={(e) => setStaffForm({...staffForm, team: e.target.value})}
                      className="bg-slate-900 border-slate-600 text-white mt-1"
                      data-testid="staff-team"
                    />
                  </div>
                </div>
                <Alert className="bg-purple-500/10 border-purple-500/30">
                  <Info className="h-4 w-4 text-purple-400" />
                  <AlertDescription className="text-slate-300 text-sm">
                    Adding staff will auto-update: Capacity Analysis, Caseload Distribution, Burnout Risk, Hiring Predictions
                  </AlertDescription>
                </Alert>
              </div>
            )}

            {/* Productivity Entry Form */}
            {dataEntryType === 'productivity' && (
              <div className="space-y-4 p-4 rounded-lg bg-slate-800/50 border border-slate-700" data-testid="productivity-form">
                <h4 className="text-sm font-medium text-amber-400 flex items-center gap-2">
                  <ClipboardList className="h-4 w-4" />
                  Productivity Data Entry
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-400 text-sm">Clinician Name *</Label>
                    <Input
                      type="text"
                      placeholder="Clinician Name"
                      value={productivityForm.clinician_name}
                      onChange={(e) => setProductivityForm({...productivityForm, clinician_name: e.target.value})}
                      className="bg-slate-900 border-slate-600 text-white mt-1"
                      data-testid="productivity-name"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">Team</Label>
                    <Input
                      type="text"
                      placeholder="Team Name"
                      value={productivityForm.team}
                      onChange={(e) => setProductivityForm({...productivityForm, team: e.target.value})}
                      className="bg-slate-900 border-slate-600 text-white mt-1"
                      data-testid="productivity-team"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">Discipline</Label>
                    <Select value={productivityForm.discipline} onValueChange={(v) => setProductivityForm({...productivityForm, discipline: v})}>
                      <SelectTrigger className="bg-slate-900 border-slate-600 text-white mt-1" data-testid="productivity-discipline">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-600">
                        <SelectItem value="RN">RN</SelectItem>
                        <SelectItem value="LPN">LPN</SelectItem>
                        <SelectItem value="RNCM">RN Case Manager</SelectItem>
                        <SelectItem value="AIDE">Aide</SelectItem>
                        <SelectItem value="MSW">Social Worker</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">Total Visits *</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 18"
                      value={productivityForm.total_visits}
                      onChange={(e) => setProductivityForm({...productivityForm, total_visits: e.target.value})}
                      className="bg-slate-900 border-slate-600 text-white mt-1"
                      data-testid="productivity-visits"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-slate-400 text-sm">Overtime Hours</Label>
                    <Input
                      type="number"
                      step="0.5"
                      placeholder="0"
                      value={productivityForm.overtime_hours}
                      onChange={(e) => setProductivityForm({...productivityForm, overtime_hours: e.target.value})}
                      className="bg-slate-900 border-slate-600 text-white mt-1"
                      data-testid="productivity-overtime"
                    />
                  </div>
                </div>
                <Alert className="bg-amber-500/10 border-amber-500/30">
                  <Info className="h-4 w-4 text-amber-400" />
                  <AlertDescription className="text-slate-300 text-sm">
                    Saving productivity will auto-update: Burnout Risk Assessment, PRN Forecast, Hiring Predictions
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </div>

          <DialogFooter className="flex gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowDataEntry(false)}
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
              data-testid="data-entry-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (dataEntryType === 'census') submitCensusEntry();
                else if (dataEntryType === 'staff') submitStaffEntry();
                else if (dataEntryType === 'productivity') submitProductivityEntry();
              }}
              disabled={dataEntryLoading}
              className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400"
              data-testid="data-entry-submit"
            >
              {dataEntryLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving & Recalculating...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save & Trigger Cascade
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* What-If Simulation Dialog */}
      <Dialog open={showSimulation} onOpenChange={setShowSimulation}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FlaskConical className="h-5 w-5 text-purple-400" />
              What-If Scenario Simulation
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Adjust parameters to simulate staffing scenarios. No changes are saved to production data.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-6">
            {/* Input Parameters */}
            <div className="space-y-6">
              <h4 className="text-sm font-semibold text-purple-400 border-b border-slate-700 pb-2">
                Simulation Parameters
              </h4>

              {/* ADC Slider */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    ADC (Census)
                  </Label>
                  <span className="text-cyan-400 font-bold">{simulationParams.adc}</span>
                </div>
                <Slider
                  value={[simulationParams.adc]}
                  onValueChange={(v) => setSimulationParams({...simulationParams, adc: v[0]})}
                  min={50}
                  max={200}
                  step={5}
                  className="cursor-pointer"
                  data-testid="sim-adc-slider"
                />
                <div className="flex justify-between text-xs text-slate-500">
                  <span>50</span>
                  <span>200</span>
                </div>
              </div>

              {/* RN FTE */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300">RN FTEs</Label>
                  <span className="text-cyan-400 font-bold">{simulationParams.rn_fte}</span>
                </div>
                <Slider
                  value={[simulationParams.rn_fte]}
                  onValueChange={(v) => setSimulationParams({...simulationParams, rn_fte: v[0]})}
                  min={1}
                  max={20}
                  step={0.5}
                  className="cursor-pointer"
                  data-testid="sim-rn-slider"
                />
              </div>

              {/* LPN FTE */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300">LPN FTEs</Label>
                  <span className="text-cyan-400 font-bold">{simulationParams.lpn_fte}</span>
                </div>
                <Slider
                  value={[simulationParams.lpn_fte]}
                  onValueChange={(v) => setSimulationParams({...simulationParams, lpn_fte: v[0]})}
                  min={1}
                  max={20}
                  step={0.5}
                  className="cursor-pointer"
                  data-testid="sim-lpn-slider"
                />
              </div>

              {/* Aide FTE */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300">Aide FTEs</Label>
                  <span className="text-cyan-400 font-bold">{simulationParams.aide_fte}</span>
                </div>
                <Slider
                  value={[simulationParams.aide_fte]}
                  onValueChange={(v) => setSimulationParams({...simulationParams, aide_fte: v[0]})}
                  min={1}
                  max={20}
                  step={0.5}
                  className="cursor-pointer"
                  data-testid="sim-aide-slider"
                />
              </div>

              {/* Daily Rate */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300 flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Daily Rate
                  </Label>
                  <span className="text-emerald-400 font-bold">${simulationParams.daily_rate}</span>
                </div>
                <Slider
                  value={[simulationParams.daily_rate]}
                  onValueChange={(v) => setSimulationParams({...simulationParams, daily_rate: v[0]})}
                  min={100}
                  max={300}
                  step={5}
                  className="cursor-pointer"
                  data-testid="sim-rate-slider"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={resetSimulation}
                  variant="outline"
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                  data-testid="sim-reset-btn"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset
                </Button>
                <Button
                  onClick={runSimulation}
                  disabled={simulationLoading}
                  className="flex-1 bg-gradient-to-r from-purple-500 to-cyan-500 hover:from-purple-400 hover:to-cyan-400"
                  data-testid="sim-run-btn"
                >
                  {simulationLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4 mr-2" />
                  )}
                  Run Simulation
                </Button>
              </div>
            </div>

            {/* Results Panel */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-emerald-400 border-b border-slate-700 pb-2">
                Simulation Results
              </h4>

              {!simulationResults ? (
                <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                  <Calculator className="h-12 w-12 mb-4 opacity-50" />
                  <p>Adjust parameters and click "Run Simulation"</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Staffing Gaps */}
                  <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                    <h5 className="text-xs text-slate-400 mb-2">STAFFING GAPS</h5>
                    <div className="space-y-2">
                      {Object.entries(simulationResults.staffing).filter(([k]) => k !== 'total_gap').map(([role, data]) => (
                        <div key={role} className="flex items-center justify-between text-sm">
                          <span className="text-white uppercase">{role}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-slate-400">{data.current} → {data.required}</span>
                            <Badge className={parseFloat(data.gap) > 0 ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'}>
                              {parseFloat(data.gap) > 0 ? '+' : ''}{data.gap}
                            </Badge>
                          </div>
                        </div>
                      ))}
                      <div className="pt-2 border-t border-slate-700 flex justify-between">
                        <span className="text-slate-400 text-sm">Total Gap</span>
                        <span className="text-red-400 font-bold">{simulationResults.staffing.total_gap} FTE</span>
                      </div>
                    </div>
                  </div>

                  {/* Revenue Projection */}
                  <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                    <h5 className="text-xs text-emerald-400 mb-2">REVENUE PROJECTION</h5>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <p className="text-xs text-slate-400">Daily</p>
                        <p className="text-lg font-bold text-white">{formatCurrency(simulationResults.revenue.daily)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Monthly</p>
                        <p className="text-lg font-bold text-white">{formatCurrency(simulationResults.revenue.monthly)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Annual</p>
                        <p className="text-lg font-bold text-emerald-400">{formatCurrency(simulationResults.revenue.annual)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Hiring & Caseload */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                      <h5 className="text-xs text-slate-400 mb-1">HIRES NEEDED</h5>
                      <p className="text-xl font-bold text-cyan-400">{simulationResults.hiring.total_hires}</p>
                      <p className="text-xs text-slate-500">Est. Cost: {formatCurrency(simulationResults.hiring.estimated_cost)}/yr</p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                      <h5 className="text-xs text-slate-400 mb-1">AVG CASELOAD</h5>
                      <p className="text-xl font-bold text-purple-400">{simulationResults.caseload.average}</p>
                      <p className="text-xs text-slate-500">Target: {simulationResults.caseload.target}</p>
                    </div>
                  </div>

                  {/* Burnout Risk */}
                  <div className={`p-3 rounded-lg border ${
                    simulationResults.burnout.level === 'low' ? 'bg-emerald-500/10 border-emerald-500/30' :
                    simulationResults.burnout.level === 'moderate' ? 'bg-amber-500/10 border-amber-500/30' :
                    simulationResults.burnout.level === 'high' ? 'bg-orange-500/10 border-orange-500/30' :
                    'bg-red-500/10 border-red-500/30'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h5 className="text-xs text-slate-400">BURNOUT RISK</h5>
                        <p className={`text-xl font-bold ${
                          simulationResults.burnout.level === 'low' ? 'text-emerald-400' :
                          simulationResults.burnout.level === 'moderate' ? 'text-amber-400' :
                          simulationResults.burnout.level === 'high' ? 'text-orange-400' :
                          'text-red-400'
                        }`}>
                          {simulationResults.burnout.level.toUpperCase()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-black text-white">{simulationResults.burnout.score}</p>
                        <p className="text-xs text-slate-500">/ 100</p>
                      </div>
                    </div>
                  </div>

                  <Alert className="bg-purple-500/10 border-purple-500/30">
                    <Sparkles className="h-4 w-4 text-purple-400" />
                    <AlertDescription className="text-slate-300 text-sm">
                      This is a simulation only. No data has been saved. Use "Quick Entry" to save real data.
                    </AlertDescription>
                  </Alert>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="pt-4">
            <Button
              variant="outline"
              onClick={() => setShowSimulation(false)}
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
              data-testid="sim-close-btn"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
